import { ProductCard } from './ProductCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorMessage } from './ui';
import { useProducts } from '../hooks/useProducts';
import { useShopping } from '../contexts/ShoppingContext';
import { useClientDataContext } from '../contexts/ClientDataContext';
import { useEffect, useRef, useCallback, useState } from 'react';
import { Product } from '../types';
import { ProductHeader } from './ProductHeader';

const PAGE_SIZE = 30;
const LS_SORT_KEY = 'preferredSort';

export function ProductGrid() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clientData } = useClientDataContext();
  const { state, dispatch, departmentsLoading, setProductsLoading } = useShopping();

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const hasEverLoadedRef = useRef(false);
  const deptEverReadyRef = useRef(false);
  const lastDispatchedProductsRef = useRef<string>('');

  // Freeze URL params when not on home page
  const isHomePage = location.pathname === '/';
  const lastHomeSearchRef = useRef(location.search);
  if (isHomePage) lastHomeSearchRef.current = location.search;
  const activeSearch = isHomePage ? location.search : lastHomeSearchRef.current;

  const urlParams = new URLSearchParams(activeSearch);
  const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : undefined;
  const selectedDepartmentId = urlParams.get('departmentId') ? Number(urlParams.get('departmentId')) : undefined;
  const searchQuery = urlParams.get('search') || undefined;
  const sortFromUrl = urlParams.get('sort') || localStorage.getItem(LS_SORT_KEY) || clientData?.defaultProductSort || 'prioritized';

  // Clean up legacy ?page= param on mount
  useEffect(() => {
    if (urlParams.has('page')) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('page');
      navigate(`/?${searchParams.toString()}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark departments ready once loaded
  if (!departmentsLoading) deptEverReadyRef.current = true;

  const hasDeptParams = !!selectedDepartmentId || !!selectedCategoryId;
  // Key representing the current URL dept/cat params
  const paramsKey = `${selectedDepartmentId ?? ''}-${selectedCategoryId ?? ''}`;
  // State that tracks which paramsKey has been validated — products only fetch once this matches
  const [readyForKey, setReadyForKey] = useState<string | null>(
    hasDeptParams ? null : paramsKey // no dept params → immediately ready
  );
  const urlReady = readyForKey === paramsKey;

  // Once departments load, validate URL params and update URL or mark ready
  // state.departments and departmentsLoading now update in the same render (no lag)
  useEffect(() => {
    if (departmentsLoading) return;

    if (!hasDeptParams) {
      setReadyForKey(paramsKey);
      return;
    }

    if (selectedDepartmentId) {
      const dept = state.departments?.find(d => d.id === selectedDepartmentId);
      if (!dept) {
        const params = new URLSearchParams(location.search);
        params.delete('departmentId');
        params.delete('categoryId');
        navigate(params.toString() ? `/?${params.toString()}` : '/', { replace: true });
        return;
      }

      if (selectedCategoryId) {
        const cat = dept.productCategories?.find(c => c.id === selectedCategoryId);
        if (!cat && (dept.productCategories?.length ?? 0) > 0) {
          const params = new URLSearchParams(location.search);
          params.delete('categoryId');
          navigate(`/?${params.toString()}`, { replace: true });
          return;
        }
      }
    }

    setReadyForKey(paramsKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentsLoading, paramsKey]);

  const filtersKey = `${selectedCategoryId}-${selectedDepartmentId}-${searchQuery}-${sortFromUrl}`;
  const [committedFiltersKey, setCommittedFiltersKey] = useState(filtersKey);
  const pendingReset = committedFiltersKey !== filtersKey;

  const { products: loadedProducts, loading: productsLoading, error, totalPages, total } = useProducts({
    departmentId: selectedDepartmentId,
    categoryId: selectedCategoryId,
    search: searchQuery,
    page: currentPage,
    sort: sortFromUrl,
    enabled: !pendingReset && urlReady,
    size: PAGE_SIZE,
  });
  const loading = productsLoading || pendingReset;
  const displayProducts = pendingReset ? [] : allProducts;

  useEffect(() => { setProductsLoading(loading); }, [loading, setProductsLoading]);

  useEffect(() => {
    if (!productsLoading && !pendingReset) {
      // Mark loaded even if empty — prevents perpetual skeleton when dept has no products
      hasEverLoadedRef.current = true;
      if (loadedProducts.length > 0) {
        setAllProducts(prev => {
          if (currentPage === 1) return loadedProducts;
          const existingIds = new Set(prev.map(p => p.id));
          return [...prev, ...loadedProducts.filter(p => !existingIds.has(p.id))];
        });
      }
    }
  }, [loadedProducts, productsLoading, pendingReset, currentPage]);

  const isLoadingMoreRef = useRef(false);
  useEffect(() => {
    isLoadingMoreRef.current = false;
    setAllProducts([]);
    setCurrentPage(1);
    const sc = document.querySelector<HTMLElement>('[data-product-scroll]');
    if (sc) sc.scrollTop = 0;
    setCommittedFiltersKey(filtersKey);
  }, [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!loading) isLoadingMoreRef.current = false; }, [loading]);

  const getScrollContainer = useCallback(
    () => document.querySelector<HTMLElement>('[data-product-scroll]'),
    []
  );

  const handleLoadMore = useCallback(() => {
    if (isLoadingMoreRef.current || loading || currentPage >= totalPages || allProducts.length === 0) return;
    isLoadingMoreRef.current = true;
    setCurrentPage(prev => prev + 1);
  }, [loading, currentPage, totalPages, allProducts.length]);

  const handleLoadMoreRef = useRef(handleLoadMore);
  useEffect(() => { handleLoadMoreRef.current = handleLoadMore; }, [handleLoadMore]);

  useEffect(() => {
    const sc = getScrollContainer();
    if (!sc) return;
    const onScroll = () => {
      if (sc.scrollHeight - sc.scrollTop - sc.clientHeight < 400) handleLoadMoreRef.current();
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [getScrollContainer]);

  useEffect(() => {
    if (loading || currentPage >= totalPages) return;
    const sc = getScrollContainer();
    if (!sc) return;
    if (sc.scrollHeight - sc.scrollTop - sc.clientHeight < 400) handleLoadMoreRef.current();
  }, [loading, currentPage, totalPages, getScrollContainer]);

  useEffect(() => {
    if (!loading && allProducts.length > 0) {
      const key = JSON.stringify(allProducts.map(p => p.id));
      if (key !== lastDispatchedProductsRef.current) {
        lastDispatchedProductsRef.current = key;
        dispatch({ type: 'SET_PRODUCTS', payload: allProducts });
      }
    }
  }, [loading, allProducts, dispatch]);

  // Show full skeleton on first load until both products and departments are ready
  const needsDept = !!selectedDepartmentId || !!selectedCategoryId;
  const everFullyReady = hasEverLoadedRef.current && (!needsDept || deptEverReadyRef.current);

  if (!everFullyReady && displayProducts.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 animate-pulse">
          {(selectedCategoryId || selectedDepartmentId || searchQuery) && (
            <nav className="mb-4" aria-hidden="true">
              <ol className="flex items-center space-x-2 text-sm">
                <li><div className="h-5 w-10 bg-gray-200 rounded" /></li>
                <li className="flex items-center">
                  <div className="w-4 h-4 bg-gray-200 rounded mx-2" />
                  <div className="h-5 w-24 bg-gray-200 rounded" />
                </li>
                {selectedCategoryId && (
                  <li className="flex items-center">
                    <div className="w-4 h-4 bg-gray-200 rounded mx-2" />
                    <div className="h-5 w-28 bg-gray-200 rounded" />
                  </li>
                )}
              </ol>
            </nav>
          )}
          <div className="h-9 w-64 bg-gray-200 rounded mb-2" />
          <div className="h-6 w-96 bg-gray-200 rounded" />
          <div className="mt-4 flex items-center gap-4">
            <div className="h-8 w-32 bg-gray-200 rounded" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
              <div className="aspect-square bg-gray-200" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3].map(j => <div key={j} className="w-4 h-4 rounded-full bg-gray-200" />)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && displayProducts.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage title="Failed to load products" message={error} onRetry={() => window?.location.reload()} />
      </div>
    );
  }

  const selectedDepartment = state.departments?.find(d => d.id === selectedDepartmentId);
  const selectedCategory = state.departments
    ?.flatMap(d => d.productCategories || [])
    .find(c => c.id === selectedCategoryId);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ProductHeader total={total} />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" role="list" aria-label="Product list">
        {displayProducts.map(product => (
          <div key={product.id} role="listitem">
            <ProductCard product={product} />
          </div>
        ))}
        {loading && (() => {
          const count = displayProducts.length === 0
            ? 12
            : Math.min(PAGE_SIZE, total - displayProducts.length);
          if (count <= 0) return null;
          return Array.from({ length: count }).map((_, i) => (
            <div key={`sk-${i}`} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse" aria-hidden="true">
              <div className="aspect-square bg-gray-200" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3].map(j => <div key={j} className="w-4 h-4 rounded-full bg-gray-200" />)}
                </div>
              </div>
            </div>
          ));
        })()}
      </div>

      {displayProducts.length === 0 && !loading && !departmentsLoading && (
        <div className="text-center py-12" role="status">
          <div className="text-gray-500 text-lg mb-2">No products found</div>
          <p className="text-gray-400">
            {!state.departments?.length
              ? 'You have not been assigned to any departments. Please contact your administrator.'
              : searchQuery
                ? `No products match your search for "${searchQuery}". Try different keywords.`
                : selectedCategory
                  ? `No products available in the ${selectedCategory.name} category.`
                  : selectedDepartment
                    ? `No products available in the ${selectedDepartment.name} department.`
                    : 'No products available at the moment.'}
          </p>
        </div>
      )}
    </div>
  );
}
